//#region Imports

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDragSortEvent,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { ButtonRadiusEnum } from '../button/types/button-radius.enum';
import { ButtonSizeEnum } from '../button/types/button-size.enum';
import { ButtonTypeEnum } from '../button/types/button-type.enum';
import { CourseModuleCardComponent } from '../course-module-card/course-module-card.component';
import { IconComponent } from '../icon/icon.component';
import { ModuleForm } from '../../models/interfaces/forms/course-form.interface';
import { NgClass } from "@angular/common";

//#endregion

@Component({
  selector: 'l-course-modules-step',
  standalone: true,
  templateUrl: './course-modules-step.component.html',
  imports: [
    ButtonComponent,
    CdkDrag,
    CdkDragPlaceholder,
    CdkDragPreview,
    CdkDropList,
    CourseModuleCardComponent,
    IconComponent,
    NgClass
],
})
export class CourseModulesStepComponent {

  //#region Public Properties

  @Input({ required: true })
  public modulesForm!: FormArray<FormGroup<ModuleForm>>;

  @Input()
  public isUpdate: boolean = false;


  @Output()
  public readonly modulesChange: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public readonly addModule: EventEmitter<void> = new EventEmitter<void>(); 

  public draggingModuleId: string | null = null;

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  //#endregion

  //#region Private Properties

  private previewOrder: string[] | null = null;

  private dragCommitted: boolean = false;

  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  //#endregion

  //#region Getters

  public get isAdvanceDisabled(): boolean {
    return this.modulesForm.length === 0;
  }

  //#endregion

  //#region Public Methods

  public isDraggingModule(moduleId: string): boolean {
    return this.draggingModuleId === moduleId;
  }

  public getDisplayIndex(moduleId: string): number {
    const order = this.previewOrder ?? this.modulesForm.controls.map((item) => item.controls.id.value);
    const index = order.indexOf(moduleId);
    return index === -1 ? 1 : index + 1;
  }

  public onAddModule(): void {
    this.addModule.emit();
  }

  public isModuleActive(moduleForm: FormGroup<ModuleForm>): boolean {
    return moduleForm.controls.isActive.value !== false;
  }

  public onModuleNameChange(moduleId: string, name: string): void {
    const moduleForm = this.findModuleForm(moduleId);
    moduleForm?.controls.name.setValue(name);
    this.modulesChange.emit();
  }

  public onModuleActiveChange(moduleId: string, isActive: boolean): void {
    const moduleForm = this.findModuleForm(moduleId);
    moduleForm?.controls.isActive.setValue(isActive);
    this.modulesChange.emit();
  }

  public onDeleteModule(moduleId: string): void {
    const moduleIndex = this.modulesForm.controls.findIndex((item) => item.controls.id.value === moduleId);

    if (moduleIndex >= 0) {
      this.modulesForm.removeAt(moduleIndex);
      this.modulesChange.emit();
    }
  }

  public onDragStartedForModule(moduleId: string): void {
    this.draggingModuleId = moduleId;
    this.dragCommitted = false;
    this.previewOrder = this.modulesForm.controls.map((item) => item.controls.id.value);
  }

  public onDragEnded(): void {
    if (!this.dragCommitted) {
      this.previewOrder = null;
      this.draggingModuleId = null;
    }

    this.dragCommitted = false;
  }

  public onListSorted(event: CdkDragSortEvent): void {
    if (!this.previewOrder) {
      return;
    }

    moveItemInArray(this.previewOrder, event.previousIndex, event.currentIndex);
    this.changeDetectorRef.markForCheck();
  }

  public onModulesReordered(event: CdkDragDrop<FormGroup<ModuleForm>[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const reordered = [...this.modulesForm.controls];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      this.modulesForm.clear();
      reordered.forEach((moduleForm, order) => {
        moduleForm.controls.order.setValue(order);
        this.modulesForm.push(moduleForm);
      });
      this.modulesChange.emit();
    }

    this.dragCommitted = true;
    this.previewOrder = null;
    this.draggingModuleId = null;
  }

  //#endregion

  //#region Private Methods

  private findModuleForm(moduleId: string): FormGroup<ModuleForm> | undefined {
    return this.modulesForm.controls.find((item) => item.controls.id.value === moduleId);
  }

  //#endregion

}
